/**
 * @fileoverview The owner's own list: every page, site and theme they publish,
 * each linked to its editor, and a way to remove one.
 *
 * "Organise" lives in the site editor, which already adds, removes and orders
 * a site's pages. This screen links to it rather than growing a second one.
 */

import type { SignerInterface } from '@cloistr/auth/core';
import { nip19 } from 'nostr-tools';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { RequireSigner } from '@/components/RequireSigner';
import { PAGE_COLLECTION_KIND, PAGE_INDEX_KIND } from '@/services/pages/kinds';
import {
  loadMine,
  removeMine,
  revealPrivateTitles,
  type MyItem,
} from '@/services/pages/myPages';

export function MyPagesRoute() {
  return (
    <RequireSigner title="Your pages" callToAction="Sign in to see and manage what you publish.">
      {(signer, pubkey) => <Mine signer={signer} pubkey={pubkey} />}
    </RequireSigner>
  );
}

function Mine({ signer, pubkey }: { signer: SignerInterface; pubkey: string }) {
  const [items, setItems] = useState<MyItem[] | null>(null);
  const [revealing, setRevealing] = useState(false);

  // KEYED ON THE PUBKEY, never on the signer object: once a session is
  // restored the auth package mints a new signer on every render, and an
  // effect depending on it would query the relays forever.
  const signerRef = useRef(signer);
  signerRef.current = signer;

  useEffect(() => {
    let live = true;
    void loadMine(pubkey).then((loaded) => {
      if (live) setItems(loaded);
    });
    return () => {
      live = false;
    };
  }, [pubkey]);

  if (items === null) return <p className="editor-note">Looking for what you have published…</p>;

  const sealed = items.filter((i) => i.private && i.title === null);

  async function reveal() {
    setRevealing(true);
    try {
      const opened = await revealPrivateTitles(items ?? [], signerRef.current);
      setItems(opened);
    } finally {
      setRevealing(false);
    }
  }

  return (
    <section className="editor">
      <h1 className="editor-heading">Your pages</h1>
      <p className="editor-note">
        <Link to="/write">Write a page</Link> · <Link to="/site">Make a site</Link> ·{' '}
        <Link to="/theme">Make a theme</Link> · <Link to="/shared">Shared with me</Link>
      </p>

      {items.length === 0 ? (
        <p className="editor-note">You have nothing published yet.</p>
      ) : (
        <>
          {sealed.length > 0 && (
            <p className="editor-note">
              Private pages are listed by their name until you open them. Each title is decrypted
              by your signer, which may ask you to approve every one.{' '}
              <button type="button" onClick={() => void reveal()} disabled={revealing}>
                {revealing ? 'Opening…' : 'Show private titles'}
              </button>
            </p>
          )}
          <ul className="share-list">
            {items.map((item) => (
              <Row
                key={item.coordinate}
                item={item}
                signer={signerRef}
                onRemoved={() =>
                  setItems((all) => (all ?? []).filter((i) => i.coordinate !== item.coordinate))
                }
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

const KIND_NAMES: Record<number, string> = {
  [PAGE_INDEX_KIND]: 'Page',
  [PAGE_COLLECTION_KIND]: 'Site',
};

function editorPath(kind: number): string {
  if (kind === PAGE_INDEX_KIND) return '/write';
  if (kind === PAGE_COLLECTION_KIND) return '/site';
  return '/theme';
}

type Removal =
  | { status: 'idle' }
  | { status: 'confirming' }
  | { status: 'sending' }
  | { status: 'failed'; reasons: string[] };

function Row({
  item,
  signer,
  onRemoved,
}: {
  item: MyItem;
  signer: { current: SignerInterface };
  onRemoved: () => void;
}) {
  const [removal, setRemoval] = useState<Removal>({ status: 'idle' });
  const address = nip19.naddrEncode({
    kind: item.kind,
    pubkey: item.pubkey,
    identifier: item.identifier,
  });
  const kindName = KIND_NAMES[item.kind] ?? 'Theme';
  const label = item.title ?? (item.private ? `Private page · ${item.identifier}` : item.identifier);

  async function remove() {
    setRemoval({ status: 'sending' });
    try {
      const outcome = await removeMine(item, signer.current);
      if (outcome.accepted.length > 0) {
        onRemoved();
        return;
      }
      setRemoval({
        status: 'failed',
        reasons: outcome.refused.map((r) => `${r.relay}: ${r.reason}`),
      });
    } catch (e) {
      setRemoval({ status: 'failed', reasons: [(e as Error).message] });
    }
  }

  return (
    <li>
      <strong>{label}</strong> <span className="editor-label">{kindName}</span>
      {item.private && item.title !== null && <span className="editor-label"> · private</span>}{' '}
      {item.kind !== PAGE_COLLECTION_KIND && item.kind !== PAGE_INDEX_KIND ? null : (
        <Link to={`/${address}`}>Open</Link>
      )}{' '}
      <Link to={`${editorPath(item.kind)}/${address}`}>Edit</Link>{' '}
      {removal.status === 'idle' && (
        <button type="button" onClick={() => setRemoval({ status: 'confirming' })}>
          Remove
        </button>
      )}
      {(removal.status === 'confirming' || removal.status === 'sending') && (
        <div className="editor-warning">
          <p>
            Removing asks the relays to stop serving this. It is a request, not a retraction:
          </p>
          <ul>
            <li>Relays we do not run may ignore it.</li>
            <li>
              Anyone who already opened, saved or mirrored it keeps their copy. Public viewers such
              as njump are known to keep pages they have shown.
            </li>
            {item.private && (
              <li>
                Anyone you shared it with keeps what they could already read. The record of who
                you shared it with is removed along with it.
              </li>
            )}
            <li>Links people hold will stop leading here.</li>
            {item.kind === PAGE_INDEX_KIND && (
              <li>Sites that list this page will show it as unavailable.</li>
            )}
            {item.kind === PAGE_COLLECTION_KIND && (
              <li>The pages in this site are not removed; only the site is.</li>
            )}
          </ul>
          <button
            type="button"
            onClick={() => void remove()}
            disabled={removal.status === 'sending'}
          >
            {removal.status === 'sending' ? 'Sending…' : 'Yes, ask the relays to remove it'}
          </button>{' '}
          <button
            type="button"
            onClick={() => setRemoval({ status: 'idle' })}
            disabled={removal.status === 'sending'}
          >
            Keep it
          </button>
        </div>
      )}
      {removal.status === 'failed' && (
        <div className="editor-result editor-result-bad">
          <p>No relay accepted the request, so nothing was removed:</p>
          <ul>
            {removal.reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
          <button type="button" onClick={() => setRemoval({ status: 'idle' })}>
            Close
          </button>
        </div>
      )}
    </li>
  );
}
